import { z } from 'zod';

import type { ScaleSummary } from '../records/types';

const uuid = z.string().uuid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');

/**
 * A score as the client sends it; the API checks it against the year's scale (its bounds and
 * its decimals).
 */
const score = z.number().min(0).max(1000);

/**
 * What an evaluation is (#106): a class activity or an exam. Where a weighting by categories
 * would attach later.
 */
export const evaluationKindSchema = z.enum(['activity', 'exam']);

/**
 * The kind of an evaluation.
 */
export type EvaluationKind = z.infer<typeof evaluationKindSchema>;

/**
 * One cell of the grade book: a group, a subject and a period (`GET /evaluations`).
 */
export const gradesCellQuerySchema = z.object({
  groupId: uuid,
  subjectId: uuid,
  periodId: uuid,
});

/**
 * Query of `GET /evaluations`.
 */
export type GradesCellQuery = z.infer<typeof gradesCellQuerySchema>;

/**
 * Body of `POST /evaluations` (scoping 3.8, #106): an activity or exam of a subject in a group
 * and period, with its relative weight and the plan topics it covers.
 */
export const createEvaluationInputSchema = z.object({
  groupId: uuid,
  subjectId: uuid,
  periodId: uuid,
  name: z.string().trim().min(1).max(120),
  kind: evaluationKindSchema.default('activity'),
  weight: z.number().positive().max(100),
  dueOn: isoDate.nullable().optional(),
  topicIds: z.array(uuid).max(20).default([]),
});

/**
 * Body of `POST /evaluations`.
 */
export type CreateEvaluationInput = z.input<typeof createEvaluationInputSchema>;

/**
 * Body of `PATCH /evaluations/{id}`: any of its fields; `topicIds` replaces the covered topics.
 */
export const updateEvaluationInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    kind: evaluationKindSchema,
    weight: z.number().positive().max(100),
    dueOn: isoDate.nullable(),
    topicIds: z.array(uuid).max(20),
  })
  .partial()
  .refine((input) => Object.keys(input).length > 0, { message: 'Nothing to update' });

/**
 * Body of `PATCH /evaluations/{id}`.
 */
export type UpdateEvaluationInput = z.input<typeof updateEvaluationInputSchema>;

/**
 * Body of `PUT /evaluations/{id}/scores`: each student's score (null clears it) and comment,
 * replacing what that student had in this evaluation, so a replay writes the same thing.
 */
export const evaluationScoresInputSchema = z.object({
  scores: z
    .array(
      z.object({
        studentId: uuid,
        score: score.nullable(),
        comment: z.string().trim().max(500).nullable().optional(),
      }),
    )
    .min(1)
    .max(200)
    .refine((rows) => new Set(rows.map((row) => row.studentId)).size === rows.length, {
      message: 'Each student appears once',
    }),
});

/**
 * Body of `PUT /evaluations/{id}/scores`.
 */
export type EvaluationScoresInput = z.input<typeof evaluationScoresInputSchema>;

/**
 * Body of `PATCH /school/period-grades`: the teacher's override of one student's period grade
 * and the observation the report card prints. `overrideScore: null` returns to the computed
 * grade; an override needs an observation.
 */
export const periodGradeAdjustmentInputSchema = z
  .object({
    groupId: uuid,
    subjectId: uuid,
    periodId: uuid,
    studentId: uuid,
    overrideScore: score.nullable().optional(),
    observation: z.string().trim().max(1000).nullable().optional(),
  })
  .refine((input) => input.overrideScore !== undefined || input.observation !== undefined, {
    path: ['overrideScore'],
    message: 'Give overrideScore, observation or both',
  });

/**
 * Body of `PATCH /school/period-grades`.
 */
export type PeriodGradeAdjustmentInput = z.input<typeof periodGradeAdjustmentInputSchema>;

/**
 * Body of `POST /school/period-grades/publish`: one subject of a group in a period, or the
 * whole group when `subjectId` is left out (the school's publication).
 */
export const publishGradesInputSchema = z.object({
  groupId: uuid,
  periodId: uuid,
  subjectId: uuid.optional(),
});

/**
 * Body of `POST /school/period-grades/publish`.
 */
export type PublishGradesInput = z.input<typeof publishGradesInputSchema>;

/**
 * Body of `POST /school/period-grades/reopen`: what the school reopens and why (audited).
 */
export const reopenGradesInputSchema = z.object({
  groupId: uuid,
  periodId: uuid,
  subjectId: uuid.optional(),
  reason: z.string().trim().min(3).max(500),
});

/**
 * Body of `POST /school/period-grades/reopen`.
 */
export type ReopenGradesInput = z.input<typeof reopenGradesInputSchema>;

/**
 * Query of `GET /students/{id}/grades`: the school year, by default the current one.
 */
export const studentGradesQuerySchema = z.object({ schoolYearId: uuid.optional() });

/**
 * Query of `GET /students/{id}/grades`.
 */
export type StudentGradesQuery = z.infer<typeof studentGradesQuerySchema>;

/**
 * An evaluation with the topics it covers and how many students have a score in it.
 */
export type EvaluationView = {
  id: string;
  groupId: string;
  subjectId: string;
  periodId: string;
  name: string;
  kind: EvaluationKind;
  weight: number;
  dueOn: string | null;
  topics: { id: string; title: string; periodPosition: number }[];
  scoredCount: number;
  createdAt: string;
  updatedAt: string;
};

/**
 * Answer of `GET /evaluations`: the cell's evaluations and whether its grades are published.
 */
export type EvaluationList = { items: EvaluationView[]; published: boolean };

/**
 * Answer of `POST /school/period-grades/publish`: the subjects published, the rows that became
 * visible to families, and how many of them have no grade.
 */
export type PublishGradesResult = {
  groupId: string;
  periodId: string;
  subjectIds: string[];
  published: number;
  withoutGrade: number;
};

/**
 * Answer of `POST /school/period-grades/reopen`.
 */
export type ReopenGradesResult = {
  groupId: string;
  periodId: string;
  subjectIds: string[];
  reopened: number;
};

/**
 * A student's published period grades in one school year, by subject (`GET /students/{id}/
 * grades`): what a guardian and the student read. Drafts never appear.
 */
export type StudentGrades = {
  studentId: string;
  schoolYear: { id: string; name: string } | null;
  scale: ScaleSummary | null;
  periods: { id: string; name: string; position: number }[];
  subjects: {
    subjectId: string;
    subjectName: string;
    grades: {
      periodId: string;
      score: number | null;
      nationalLabel: string | null;
      observation: string | null;
      publishedAt: string | null;
    }[];
  }[];
};
